import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Send, Beaker, AlertTriangle, CheckCircle2, KeyRound, ShieldCheck, ShieldAlert, RefreshCw, Lock, Crown, Users, Award, Download } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const TOKEN_KEY = "roadrig.admin.token";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const blank = {
  slug: "",
  tag: "ENTRY 07",
  date_label: "",
  title: "",
  body: "",
  image_url: "",
  object_pos: "50% 50%",
};

export default function AdminJournal() {
  const [token, setToken] = useState("");
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [foundingRiders, setFoundingRiders] = useState(null);
  const [foundingLoading, setFoundingLoading] = useState(false);

  const refreshFoundingRiders = async () => {
    if (!token) return;
    setFoundingLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/founding-riders`, {
        headers: { "X-Admin-Token": token },
      });
      setFoundingRiders(data);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(`Founding Riders: ${detail}`);
      setFoundingRiders({ founding_riders: [], totals: { active_referrers: 0, total_referrals: 0, total_signups: 0 } });
    } finally {
      setFoundingLoading(false);
    }
  };

  const refreshHealth = async () => {
    setHealthLoading(true);
    try {
      const { data } = await axios.get(`${API}/health`);
      setHealth(data);
    } catch {
      setHealth({ status: "unreachable", resend_configured: false, test_mode_default: true });
    } finally {
      setHealthLoading(false);
    }
  };

  useEffect(() => {
    document.title = "ROADRIG · Admin · Journal";
    const t = localStorage.getItem(TOKEN_KEY);
    if (t) setToken(t);
    refreshHealth();
  }, []);

  const saveToken = (val) => {
    setToken(val);
    if (val) localStorage.setItem(TOKEN_KEY, val);
    else localStorage.removeItem(TOKEN_KEY);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (mode, dry_run = false) => {
    if (!token) return toast.error("Paste your admin token first.");
    if (!form.slug || !form.title || !form.image_url) {
      return toast.error("slug, title and image_url are required.");
    }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await axios.post(
        `${API}/journal/publish`,
        { ...form, mode, dry_run },
        { headers: { "X-Admin-Token": token, "Content-Type": "application/json" } }
      );
      setResult(data);
      const n = data.notifications;
      if (dry_run) {
        toast.success(`Dry run · ${n.recipients_total} recipient(s) targeted`);
      } else if (mode === "test") {
        toast.success(`Test send · ${n.sent}/${n.recipients_total} sent to ${form.slug ? "test address" : "—"}`);
      } else {
        toast.success(`LIVE · ${n.sent} sent · ${n.failed} failed`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(`Failed: ${detail}`);
      setResult({ error: detail });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-journal-page" className="min-h-screen bg-[#0A0A0C] text-white">
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-12">
        <div className="flex items-center justify-between mb-10">
          <Link to="/" className="serial text-[10px] text-white/60 hover:text-white flex items-center gap-2">
            <ArrowLeft size={12} /> Back to Site
          </Link>
          <span className="h-display text-xl">ROAD<span className="text-[#F47A20]">RIG</span> · Admin</span>
        </div>

        <div className="flex items-center gap-3 mb-4">
          <span className="section-mark" />
          <span className="overline">Publish Journal Entry</span>
        </div>
        <h1 className="h-section text-white text-3xl sm:text-4xl mb-2">Publish &amp; notify the VIP list.</h1>
        <p className="text-white/55 mb-8 max-w-2xl">
          Add a new Founder&apos;s Journal entry and fan-out an email to your VIP waitlist. Test mode sends only to{" "}
          <span className="text-[#F47A20]">your test address</span>. Go live only when you&apos;re ready.
        </p>

        {/* MODE BANNER */}
        {(() => {
          const inTest = !health || health.test_mode_default || !health.resend_configured;
          const stripe = inTest ? "border-[#F47A20] bg-[#F47A20]/10" : "border-red-500 bg-red-500/10";
          const ic = inTest ? <ShieldCheck size={18} className="text-[#F47A20]" /> : <ShieldAlert size={18} className="text-red-400" />;
          const headline = inTest ? "EMAILS IN TEST MODE" : "EMAILS LIVE — REAL VIPs WILL RECEIVE THIS";
          const detail = !health
            ? "Health check loading…"
            : health.status === "unreachable"
            ? "Health endpoint unreachable. Assuming test mode."
            : !health.resend_configured
            ? "RESEND_API_KEY is blank. All sends safely no-op."
            : health.test_mode_default
            ? "JOURNAL_TEST_MODE=true. Every send is routed to JOURNAL_TEST_TO."
            : "JOURNAL_TEST_MODE=false AND Resend is configured. Next live publish reaches the real VIP list.";
          return (
            <div data-testid="admin-mode-banner" data-mode={inTest ? "test" : "live"} className={`mb-8 p-4 md:p-5 border rounded-sm flex flex-col md:flex-row md:items-center gap-3 ${stripe}`}>
              <div className="flex items-center gap-3 flex-shrink-0">
                {ic}
                <div>
                  <div data-testid="admin-mode-headline" className="serial text-[11px] tracking-[0.25em] font-bold" style={{ color: inTest ? "#F47A20" : "#FCA5A5" }}>{headline}</div>
                  <div className="text-xs text-white/65">{detail}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 md:ml-auto text-[10px] serial text-white/40">
                <span>resend: <span className="text-white/70">{health?.resend_configured ? "configured" : "blank"}</span></span>
                <span>·</span>
                <span>test_mode_default: <span className="text-white/70">{String(health?.test_mode_default ?? true)}</span></span>
                <button data-testid="admin-mode-refresh" onClick={refreshHealth} disabled={healthLoading} className="ml-2 inline-flex items-center gap-1 px-2 py-1 border border-white/15 rounded-sm hover:border-white/40 transition-colors">
                  <RefreshCw size={11} className={healthLoading ? "animate-spin" : ""} /> Refresh
                </button>
              </div>
            </div>
          );
        })()}

        {/* Admin token */}
        <div className="mb-8 p-5 bg-[#141417] border border-white/10 rounded-sm">
          <label className="serial text-[10px] text-[#F47A20] flex items-center gap-2 mb-3">
            <KeyRound size={12} /> Admin Token
          </label>
          <input
            data-testid="admin-token-input"
            type="password"
            placeholder="Paste JOURNAL_ADMIN_TOKEN from backend/.env"
            value={token}
            onChange={(e) => saveToken(e.target.value)}
            className="w-full bg-[#0A0A0C] border border-white/10 rounded-sm px-4 py-3 text-white placeholder:text-white/30 focus:border-[#F47A20] focus:ring-1 focus:ring-[#F47A20] outline-none font-mono text-sm"
          />
          <p className="text-white/40 text-xs mt-2">Stored locally in your browser only. Never sent except as the X-Admin-Token header.</p>
        </div>

        {/* Form */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput label="Slug (unique, kebab-case)" testid="adm-slug" value={form.slug} onChange={set("slug")} placeholder="materials-confirmed" />
          <FormInput label="Tag" testid="adm-tag" value={form.tag} onChange={set("tag")} placeholder="ENTRY 07" />
          <FormInput label="Date Label" testid="adm-date" value={form.date_label} onChange={set("date_label")} placeholder="Materials Confirmed" />
          <FormInput label="Object Position (CSS)" testid="adm-pos" value={form.object_pos} onChange={set("object_pos")} placeholder="50% 50%" />
          <FormInput label="Title" testid="adm-title" value={form.title} onChange={set("title")} placeholder="1680D ballistic nylon locked in." className="md:col-span-2" />
          <FormInput label="Image URL" testid="adm-image" value={form.image_url} onChange={set("image_url")} placeholder="https://..." className="md:col-span-2" />
          <div className="md:col-span-2">
            <label className="serial text-[10px] text-white/50 block mb-2">Body</label>
            <textarea
              data-testid="adm-body"
              rows={6}
              value={form.body}
              onChange={set("body")}
              placeholder="Three months of sample testing later, we locked the exterior fabric..."
              className="w-full bg-[#141417] border border-white/10 rounded-sm px-4 py-3 text-white placeholder:text-white/30 focus:border-[#F47A20] focus:ring-1 focus:ring-[#F47A20] outline-none"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="mt-8 flex flex-col md:flex-row flex-wrap gap-3">
          <button data-testid="adm-dry-run" disabled={busy} onClick={() => submit("test", true)} className="btn-secondary">
            <Beaker size={16} /> Dry Run (preview recipients)
          </button>
          <button data-testid="adm-test-send" disabled={busy} onClick={() => submit("test", false)} className="btn-secondary">
            <Send size={16} /> Test Send (to my email)
          </button>
          <button
            data-testid="adm-live-send"
            disabled={busy}
            onClick={() => {
              if (!window.confirm("This will fan-out the email to your entire VIP list. Continue?")) return;
              submit("live", false);
            }}
            className="btn-primary"
          >
            <AlertTriangle size={16} /> Publish &amp; Send Live
          </button>
        </div>

        {/* Result */}
        {result && (
          <div data-testid="adm-result" className="mt-10 p-5 bg-[#141417] border border-white/10 rounded-sm">
            <div className="flex items-center gap-2 mb-3">
              {result.error ? (
                <AlertTriangle size={14} className="text-red-400" />
              ) : (
                <CheckCircle2 size={14} className="text-[#F47A20]" />
              )}
              <span className="serial text-[10px] text-white/60">RESPONSE</span>
            </div>
            <pre className="text-xs text-white/80 overflow-x-auto whitespace-pre-wrap font-mono">{JSON.stringify(result, null, 2)}</pre>
          </div>
        )}

        {/* Setup quick ref */}
        <div className="mt-12 p-6 bg-[#141417] border border-white/10 rounded-sm text-sm text-white/70 leading-relaxed">
          <div className="serial text-[10px] text-[#F47A20] mb-3">SETUP CHECKLIST</div>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Resend account &rarr; add domain <code className="text-white">roadrig.com.au</code> &rarr; copy DNS records.</li>
            <li>Add SPF, DKIM, MX/Return-Path records to your DNS (see /app/memory/journal-emails.md).</li>
            <li>Generate Resend API key (starts with <code className="text-white">re_</code>).</li>
            <li>Set <code className="text-white">RESEND_API_KEY</code> in <code className="text-white">/app/backend/.env</code>, restart backend.</li>
            <li>Run a <strong>Dry Run</strong> first, then a <strong>Test Send</strong>, then go Live.</li>
          </ol>
        </div>

        {/* VIP Priority Access — placeholder, activates at OUTLAW V1 launch */}
        <div data-testid="vip-priority-access-panel" className="mt-12 p-6 bg-[#141417] border border-white/10 rounded-sm">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Crown size={14} className="text-[#F47A20]" />
              <span className="serial text-[10px] tracking-[0.25em] text-[#F47A20] font-bold">VIP PRIORITY ACCESS</span>
            </div>
            <span data-testid="vip-priority-access-status" className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-white/15 text-white/55 text-[10px] tracking-[0.25em] uppercase font-bold rounded-sm">
              <Lock size={11} /> Placeholder &middot; Activates at OUTLAW V1 launch
            </span>
          </div>

          <h2 className="text-white text-xl md:text-2xl font-extrabold tracking-tight mb-2">VIP launch controls</h2>
          <p className="text-white/55 text-sm leading-relaxed max-w-2xl mb-6">
            This is where you&apos;ll flip the switch on launch day. Until then, every VIP keeps stacking up in member-number order and the perks below are promised in the welcome email. <span className="text-white/80">No discount system &mdash; exclusivity only.</span>
          </p>

          {/* Perks manifest */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-7">
            {[
              { n: "01", t: "Unique founding-member number", d: "Assigned at signup. Sequential. Permanent." },
              { n: "02", t: "Priority access to OUTLAW V1 launch", d: "VIPs see the live store before anyone else." },
              { n: "03", t: "Early pre-order access", d: "Reservations open to VIPs in member-number order, then public." },
              { n: "04", t: "Founder Journal updates", d: "Already wired &mdash; every published entry fans out to VIPs." },
              { n: "05", t: "Behind-the-scenes dev updates", d: "Workshop notes, sample reveals, factory videos." },
            ].map((p) => (
              <div key={p.n} data-testid={`vip-perk-${p.n}`} className="p-4 bg-[#0E0E11] border border-white/8 rounded-sm">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="serial text-[10px] text-[#F47A20] font-bold">{p.n}</span>
                  <span className="text-white text-sm font-bold">{p.t}</span>
                </div>
                <p className="text-white/55 text-xs leading-relaxed pl-6">{p.d}</p>
              </div>
            ))}
          </div>

          {/* Disabled launch toggle row */}
          <div className="p-4 border border-dashed border-white/15 rounded-sm bg-[#0E0E11]">
            <div className="serial text-[10px] text-white/40 tracking-[0.25em] mb-3">LAUNCH CONTROLS (DISABLED UNTIL LAUNCH)</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <button data-testid="vip-toggle-priority-access" disabled className="opacity-50 cursor-not-allowed text-left p-3 border border-white/10 rounded-sm bg-[#141417]">
                <div className="text-white text-sm font-bold mb-0.5">Enable Priority Access</div>
                <div className="text-white/45 text-xs">Opens the OUTLAW V1 store to VIPs before public.</div>
              </button>
              <button data-testid="vip-toggle-preorders" disabled className="opacity-50 cursor-not-allowed text-left p-3 border border-white/10 rounded-sm bg-[#141417]">
                <div className="text-white text-sm font-bold mb-0.5">Open VIP Pre-orders</div>
                <div className="text-white/45 text-xs">Sends pre-order invites in member-number order.</div>
              </button>
              <button data-testid="vip-toggle-public-launch" disabled className="opacity-50 cursor-not-allowed text-left p-3 border border-white/10 rounded-sm bg-[#141417]">
                <div className="text-white text-sm font-bold mb-0.5">Open Public Launch</div>
                <div className="text-white/45 text-xs">Removes VIP gate. Store goes public.</div>
              </button>
            </div>
            <p className="serial text-[10px] text-white/40 mt-4">
              These controls will be wired to the storefront in Phase 3. No discount logic by design &mdash; the value is exclusivity and order of entry.
            </p>
          </div>
        </div>

        {/* VIP list export — one-click CSV download */}
        <div data-testid="vip-list-export-panel" className="mt-12 p-6 bg-[#141417] border border-white/10 rounded-sm">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Users size={14} className="text-[#F47A20]" />
              <span className="serial text-[10px] tracking-[0.25em] text-[#F47A20] font-bold">VIP LIST EXPORT</span>
            </div>
          </div>
          <p className="text-white/60 text-sm leading-relaxed mb-4 max-w-2xl">
            Download the complete VIP crew list as a CSV — includes member number, VIP code, email, source, welcome-sent status, referrer, and join date.
          </p>
          <button
            data-testid="vip-list-download-csv"
            disabled={!token}
            onClick={async () => {
              if (!token) return;
              try {
                const res = await fetch(`${API}/email-signups.csv`, { headers: { "X-Admin-Token": token } });
                if (!res.ok) { toast.error(`Export failed: HTTP ${res.status}`); return; }
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                const cd = res.headers.get("Content-Disposition") || "";
                const m = cd.match(/filename="?([^"]+)"?/);
                a.download = m ? m[1] : `roadrig-vip-list.csv`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
                toast.success("VIP list downloaded.");
              } catch (e) {
                toast.error(e?.message || "Export failed.");
              }
            }}
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#F47A20]/40 bg-[#F47A20]/[0.06] hover:bg-[#F47A20]/[0.12] hover:border-[#F47A20] transition-colors rounded-sm text-white disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Download size={14} />
            <span className="text-[12px] tracking-[0.15em] uppercase font-bold">Download VIP List (CSV)</span>
          </button>
          {!token && <p className="serial text-[10px] text-white/40 mt-3">Paste your admin token above to enable download.</p>}
        </div>

        {/* Founding Riders — referral roll-up, recognition-based */}
        <div data-testid="founding-riders-panel" className="mt-12 p-6 bg-[#141417] border border-white/10 rounded-sm">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Award size={14} className="text-[#F47A20]" />
              <span className="serial text-[10px] tracking-[0.25em] text-[#F47A20] font-bold">FOUNDING RIDERS</span>
            </div>
            <button
              data-testid="founding-riders-refresh"
              onClick={refreshFoundingRiders}
              disabled={!token || foundingLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/15 text-white/70 text-[10px] tracking-[0.25em] uppercase font-bold rounded-sm hover:border-[#F47A20]/55 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw size={11} className={foundingLoading ? "animate-spin" : ""} />
              {foundingRiders ? "Refresh" : "Load"}
            </button>
          </div>

          <h2 className="text-white text-xl md:text-2xl font-extrabold tracking-tight mb-2">Riders who brought riders.</h2>
          <p className="text-white/55 text-sm leading-relaxed max-w-2xl mb-6">
            Tracked silently when a VIP shares a journal entry. <span className="text-white/80">No commissions, no discounts.</span> Use this list to recognise early supporters &mdash; Founding Rider badges, prototype invites, launch events, exclusive merch, priority access to future drops.
          </p>

          {/* Stats */}
          {foundingRiders && (
            <div className="grid grid-cols-3 gap-3 mb-6" data-testid="founding-riders-stats">
              <div className="p-3 bg-[#0E0E11] border border-white/8 rounded-sm">
                <div className="serial text-[9px] tracking-[0.25em] text-white/45 mb-1">TOTAL SIGNUPS</div>
                <div data-testid="founding-stat-total-signups" className="h-display text-2xl text-white leading-none">{foundingRiders.totals.total_signups}</div>
              </div>
              <div className="p-3 bg-[#0E0E11] border border-white/8 rounded-sm">
                <div className="serial text-[9px] tracking-[0.25em] text-white/45 mb-1">REFERRED IN</div>
                <div data-testid="founding-stat-total-referrals" className="h-display text-2xl text-[#F47A20] leading-none">{foundingRiders.totals.total_referrals}</div>
              </div>
              <div className="p-3 bg-[#0E0E11] border border-white/8 rounded-sm">
                <div className="serial text-[9px] tracking-[0.25em] text-white/45 mb-1">ACTIVE RIDERS</div>
                <div data-testid="founding-stat-active" className="h-display text-2xl text-white leading-none">{foundingRiders.totals.active_referrers}</div>
              </div>
            </div>
          )}

          {/* List */}
          <div className="border border-white/8 rounded-sm bg-[#0E0E11] overflow-hidden">
            {!foundingRiders ? (
              <div className="p-8 text-center text-white/55 text-sm">
                <Users size={20} className="mx-auto mb-3 text-white/35" />
                {token ? "Click Load to fetch the Founding Riders roll-up." : "Paste your admin token above, then click Load."}
              </div>
            ) : foundingRiders.founding_riders.length === 0 ? (
              <div data-testid="founding-riders-empty" className="p-8 text-center text-white/55 text-sm">
                <Award size={20} className="mx-auto mb-3 text-white/35" />
                No referred signups yet. The moment a VIP shares a journal link and a friend joins, they&rsquo;ll appear here.
              </div>
            ) : (
              <ul className="divide-y divide-white/8" data-testid="founding-riders-list">
                {foundingRiders.founding_riders.map((r) => (
                  <li key={r.member_number} data-testid={`founding-rider-${r.member_number}`} className="p-4 md:p-5">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 grid place-items-center bg-[#F47A20]/10 border border-[#F47A20]/35 rounded-sm flex-shrink-0">
                          <Crown size={14} className="text-[#F47A20]" />
                        </div>
                        <div className="min-w-0">
                          <div className="serial text-[10px] text-[#F47A20] font-bold tracking-[0.25em]">{r.vip_code}</div>
                          <div className="text-white text-sm font-bold truncate">{r.email || "—"}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="h-display text-2xl text-white leading-none">{r.referrals_count}</div>
                        <div className="serial text-[9px] tracking-[0.25em] text-white/45 mt-1">{r.referrals_count === 1 ? "RIDER BROUGHT IN" : "RIDERS BROUGHT IN"}</div>
                      </div>
                    </div>
                    {r.referred?.length > 0 && (
                      <ul className="mt-3 pl-13 space-y-1.5">
                        {r.referred.map((ref) => (
                          <li key={`${r.member_number}-${ref.member_number}`} className="text-white/65 text-xs flex items-center gap-2">
                            <span className="serial text-[9px] text-[#F47A20] font-bold">VIP-{String(ref.member_number).padStart(4, "0")}</span>
                            <span>&middot;</span>
                            <span className="truncate">{ref.email}</span>
                            {ref.source && (
                              <>
                                <span className="text-white/30">&middot;</span>
                                <span className="serial text-[9px] text-white/40 tracking-[0.15em] uppercase">{ref.source}</span>
                              </>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <p className="serial text-[10px] text-white/40 mt-5 leading-relaxed">
            Future recognition framework (community-led, never sales-led): Founding Rider badge &middot; Early prototype invitations &middot; Launch event invitations &middot; Exclusive ROADRIG merchandise &middot; Priority access to future product releases.
          </p>
        </div>
      </div>
    </div>
  );
}

function FormInput({ label, value, onChange, placeholder, testid, className = "" }) {
  return (
    <div className={className}>
      <label className="serial text-[10px] text-white/50 block mb-2">{label}</label>
      <input
        data-testid={testid}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full bg-[#141417] border border-white/10 rounded-sm px-4 py-3 text-white placeholder:text-white/30 focus:border-[#F47A20] focus:ring-1 focus:ring-[#F47A20] outline-none"
      />
    </div>
  );
}
